<template>
  <div class="pagos-container">
    <!-- Header -->
    <div class="page-header">
      <div class="header-content">
        <h1 class="page-title">
          <i class="fas fa-credit-card me-2"></i>
          Gestión de Pagos del Sistema
        </h1>
        <p class="page-subtitle">
          <i class="fas fa-chart-line me-1"></i>
          Control y seguimiento de transacciones financieras
        </p>
      </div>
      <div class="header-actions">
        <button type="button" class="btn-success-custom" @click="exportarReporte">
          <i class="fas fa-file-excel me-1"></i>
          Exportar
        </button>
        <button type="button" class="btn-primary-custom" @click="procesarPagosPendientes">
          <i class="fas fa-sync-alt me-1"></i>
          Procesar Pendientes
        </button>
      </div>
    </div>

    <div class="content-wrapper">
      <!-- Métricas de pagos -->
      <div class="metrics-grid">
        <div class="metric-card metric-completed">
          <div class="metric-icon">
            <i class="fas fa-check-circle"></i>
          </div>
          <div class="metric-content">
            <div class="metric-number">
              <span v-if="loadingMetricas" class="loading-spinner"></span>
              <span v-else>${{ metricas.completados_mes }}</span>
            </div>
            <div class="metric-label">Completados Este Mes</div>
          </div>
        </div>
        <div class="metric-card metric-pending">
          <div class="metric-icon">
            <i class="fas fa-clock"></i>
          </div>
          <div class="metric-content">
            <div class="metric-number">
              <span v-if="loadingMetricas" class="loading-spinner"></span>
              <span v-else>{{ metricas.pendientes }}</span>
            </div>
            <div class="metric-label">Pagos Pendientes</div>
          </div>
        </div>
        <div class="metric-card metric-failed">
          <div class="metric-icon">
            <i class="fas fa-times-circle"></i>
          </div>
          <div class="metric-content">
            <div class="metric-number">
              <span v-if="loadingMetricas" class="loading-spinner"></span>
              <span v-else>{{ metricas.fallidos }}</span>
            </div>
            <div class="metric-label">Pagos Fallidos</div>
          </div>
        </div>
        <div class="metric-card metric-refund">
          <div class="metric-icon">
            <i class="fas fa-undo-alt"></i>
          </div>
          <div class="metric-content">
            <div class="metric-number">
              <span v-if="loadingMetricas" class="loading-spinner"></span>
              <span v-else>{{ metricas.reembolsos }}</span>
            </div>
            <div class="metric-label">Reembolsos</div>
          </div>
        </div>
      </div>

      <!-- Filtros -->
      <div class="filters-section">
        <div class="filter-group">
          <label class="filter-label">Estado</label>
          <select class="filter-select" v-model="filtros.estado">
            <option value="">Todos</option>
            <option value="completado">Completado</option>
            <option value="pendiente">Pendiente</option>
            <option value="en_verificacion">En verificación</option>
            <option value="fallido">Fallido</option>
            <option value="reembolsado">Reembolsado</option>
          </select>
        </div>
        <div class="filter-group">
          <label class="filter-label">Suscripción</label>
          <select class="filter-select" v-model="filtros.metodo">
            <option value="">Todos</option>
            <option value="mensual">Mensual</option>
            <option value="anual">Anual</option>
          </select>
        </div>
        <div class="filter-group">
          <label class="filter-label">Desde</label>
          <input type="date" class="filter-input" v-model="filtros.fecha_desde">
        </div>
        <div class="filter-group">
          <label class="filter-label">Hasta</label>
          <input type="date" class="filter-input" v-model="filtros.fecha_hasta">
        </div>
        <div class="filter-group">
          <label class="filter-label">Empresa</label>
          <input type="text" class="filter-input" v-model="filtros.empresa" placeholder="Buscar...">
        </div>
        <div class="filter-group filter-actions">
          <button type="button" class="btn-secondary-custom" @click="aplicarFiltros">
            <i class="fas fa-filter"></i>
          </button>
          <button type="button" class="btn-danger-outline" @click="limpiarFiltros">
            <i class="fas fa-times"></i>
          </button>
        </div>
      </div>

      <!-- Tabla de pagos -->
      <div class="table-section">
        <div class="table-container">
          <table class="data-table">
            <thead>
              <tr>
                <th>ID Transacción</th>
                <th>Empresa</th>
                <th>Fecha</th>
                <th>Suscripción</th>
                <th>Monto</th>
                <th>Referencia</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="pago in pagosFiltrados" :key="pago.id">
                <td>
                  <code class="transaction-id">{{ pago.id_transaccion }}</code>
                </td>
                <td>
                  <div class="company-name">{{ pago.empresa_nombre }}</div>
                </td>
                <td class="payment-date">{{ formatDate(pago.fecha_pago) }}</td>
                <td>
                  <div class="subscription-type">{{ pago.suscripcion_plan || 'Mensual' }}</div>
                </td>
                <td>
                  <div class="amount-info">
                    <div class="amount-value">${{ pago.valor }}</div>
                    <div class="amount-currency">{{ pago.moneda }}</div>
                  </div>
                </td>
                <td>
                  <div class="reference-info">{{ pago.referencia_externa || '-' }}</div>
                </td>
                <td>
                  <span class="status-badge" :class="getEstadoBadgeClass(pago.estado)">
                    {{ pago.estado }}
                  </span>
                  <div v-if="pago.estado === 'fallido' && pago.mensaje_error" class="error-message">
                    {{ pago.mensaje_error }}
                  </div>
                </td>
                <td>
                  <div class="action-buttons">
                    <button 
                      v-if="pago.estado === 'pendiente'" 
                      class="action-btn action-validate" 
                      @click="abrirValidarPago(pago)"
                      title="Validar pago"
                    >
                      <i class="fas fa-user-check"></i>
                    </button>
                    <button 
                      v-if="pago.estado === 'en_verificacion'" 
                      class="action-btn action-validate" 
                      @click="abrirValidarPago(pago)"
                      title="Validar pago"
                    >
                      <i class="fas fa-user-check"></i>
                    </button>
                    <button 
                      v-if="pago.estado === 'completado'" 
                      class="action-btn action-refund" 
                      @click="reembolsarPago(pago.id)"
                      title="Reembolsar"
                    >
                      <i class="fas fa-undo"></i>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Paginación -->
      <div class="pagination-section">
        <div class="pagination-wrapper">
          <button 
            class="pagination-btn" 
            :class="{ disabled: paginacion.pagina_actual === 1 }"
            @click="cambiarPagina(paginacion.pagina_actual - 1)"
            :disabled="paginacion.pagina_actual === 1"
          >
            <i class="fas fa-chevron-left"></i>
            Anterior
          </button>
          <div class="pagination-pages">
            <button 
              v-for="pagina in paginacion.paginas_visibles" 
              :key="pagina"
              class="pagination-page" 
              :class="{ active: pagina === paginacion.pagina_actual }"
              @click="cambiarPagina(pagina)"
            >
              {{ pagina }}
            </button>
          </div>
          <button 
            class="pagination-btn" 
            :class="{ disabled: paginacion.pagina_actual === paginacion.total_paginas }"
            @click="cambiarPagina(paginacion.pagina_actual + 1)"
            :disabled="paginacion.pagina_actual === paginacion.total_paginas"
          >
            Siguiente
            <i class="fas fa-chevron-right"></i>
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Modal Detalle del Pago -->
  <div class="modal-overlay" v-if="showDetalleModal" @click="showDetalleModal = false">
    <div class="modal-container" @click.stop>
      <div class="modal-header">
        <h3 class="modal-title">Detalle del Pago</h3>
        <button type="button" class="modal-close" @click="showDetalleModal = false">
          <i class="fas fa-times"></i>
        </button>
      </div>
      <div class="modal-body" v-if="pagoSeleccionado">
        <div class="modal-info-grid">
          <div class="info-section">
            <h4>Información del Pago</h4>
            <div class="info-item">
              <span class="info-label">ID Transacción:</span>
              <code class="info-value transaction-code">{{ pagoSeleccionado.id_transaccion }}</code>
            </div>
            <div class="info-item">
              <span class="info-label">Monto:</span>
              <span class="info-value">${{ pagoSeleccionado.monto }} {{ pagoSeleccionado.moneda }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Estado:</span>
              <span class="status-badge" :class="getEstadoBadgeClass(pagoSeleccionado.estado)">
                {{ pagoSeleccionado.estado }}
              </span>
            </div>
            <div class="info-item">
              <span class="info-label">Método:</span>
              <span class="info-value">{{ pagoSeleccionado.metodo_pago || '-' }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Referencia:</span>
              <span class="info-value">{{ pagoSeleccionado.referencia_externa || '-' }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Fecha:</span>
              <span class="info-value">{{ formatDateTime(pagoSeleccionado.fecha_pago) }}</span>
            </div>
          </div>
          <div class="info-section">
            <h4>Información de la Empresa</h4>
            <div class="info-item">
              <span class="info-label">Empresa:</span>
              <span class="info-value">{{ pagoSeleccionado.empresa_nombre }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Email:</span>
              <span class="info-value">{{ pagoSeleccionado.empresa_email }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Plan:</span>
              <span class="info-value">{{ pagoSeleccionado.suscripcion_plan }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Período:</span>
              <span class="info-value">{{ pagoSeleccionado.periodo_facturado }}</span>
            </div>
          </div>
        </div>
        
        <div v-if="pagoSeleccionado.detalles_gateway">
          <div class="modal-divider"></div>
          <div class="gateway-section">
            <h4>Detalles del Gateway</h4>
            <div class="gateway-details">
              <pre>{{ JSON.stringify(pagoSeleccionado.detalles_gateway, null, 2) }}</pre>
            </div>
          </div>
        </div>
        
        <div class="modal-footer">
          <button type="button" class="btn-secondary-custom" @click="showDetalleModal = false">
            <i class="fas fa-times me-1"></i>
            Cerrar
          </button>
          <button type="button" class="btn-primary-custom" @click="descargarRecibo(pagoSeleccionado.id)">
            <i class="fas fa-download me-1"></i>
            Descargar Recibo
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Modal Validar Pago -->
  <div class="modal-overlay" v-if="showValidarModal" @click="cerrarValidarModal">
    <div class="modal-container validar-pago-modal" @click.stop>
      <div class="modal-header">
        <h3 class="modal-title">
          <i class="fas fa-user-check me-2"></i>
          Validar Pago
        </h3>
        <button class="modal-close" @click="cerrarValidarModal">
          <i class="fas fa-times"></i>
        </button>
      </div>
      
      <div class="modal-body" v-if="pagoAValidar">
        <!-- Información de la transacción -->
        <div class="transaction-info">
          <div class="info-grid">
            <div class="info-item">
              <label class="info-label">ID Transacción:</label>
              <span class="info-value">{{ pagoAValidar.id_transaccion }}</span>
            </div>
            <div class="info-item">
              <label class="info-label">Empresa:</label>
              <span class="info-value">{{ pagoAValidar.empresa_nombre }}</span>
            </div>
            <div class="info-item">
              <label class="info-label">Fecha Registro:</label>
              <span class="info-value">{{ formatDate(pagoAValidar.fecha_pago) }}</span>
            </div>
            <div class="info-item">
              <label class="info-label">Suscripción:</label>
              <span class="info-value">{{ pagoAValidar.suscripcion_plan || 'Mensual' }}</span>
            </div>
            <div class="info-item">
              <label class="info-label">Monto Esperado:</label>
              <span class="info-value amount">${{ pagoAValidar.valor }} {{ pagoAValidar.moneda }}</span>
            </div>
            <div class="info-item">
              <label class="info-label">Estado Actual:</label>
              <span class="status-badge" :class="getEstadoBadgeClass(pagoAValidar.estado)">
                {{ pagoAValidar.estado }}
              </span>
            </div>
          </div>
        </div>

        <!-- Formulario de validación -->
        <form @submit.prevent="validarPago" class="validation-form">
          <div class="form-section">
            <h4 class="section-title">
              <i class="fas fa-check-circle me-2"></i>
              Información de Validación
            </h4>
            
            <div class="form-grid">
              <div class="form-group">
                <label class="form-label">Fecha del Pago *</label>
                <input 
                  type="date" 
                  v-model="validationForm.fecha_pago_real" 
                  class="form-input"
                  required
                />
              </div>
              
              <div class="form-group">
                <label class="form-label">Monto Depositado *</label>
                <input 
                  type="number" 
                  v-model="validationForm.monto_depositado" 
                  class="form-input"
                  step="0.01"
                  min="0"
                  required
                />
              </div>
              
              <div class="form-group">
                <label class="form-label">Código de Transacción *</label>
                <input 
                  type="text" 
                  v-model="validationForm.codigo_transaccion" 
                  class="form-input"
                  required
                />
              </div>
              
              <div class="form-group">
                <label class="form-label">Método de Pago *</label>
                <select 
                  v-model="validationForm.metodo_pago" 
                  class="form-input"
                  required
                >
                  <option value="">Seleccionar método</option>
                  <option value="efectivo">Efectivo</option>
                  <option value="transferencia">Transferencia Bancaria</option>
                  <option value="tarjeta_credito">Tarjeta de Crédito</option>
                  <option value="tarjeta_debito">Tarjeta de Débito</option>
                  <option value="cheque">Cheque</option>
                  <option value="otro">Otro</option>
                </select>
              </div>
            </div>
          </div>

          <!-- Sección de imagen -->
          <div class="form-section">
            <h4 class="section-title">
              <i class="fas fa-image me-2"></i>
              Comprobante de Pago
            </h4>
            
            <div class="image-section">
              <div v-if="validationForm.imagen_url" class="image-preview">
                <img 
                  :src="validationForm.imagen_url" 
                  alt="Comprobante" 
                  class="preview-image"
                  @error="onImageError"
                  @load="onImageLoad"
                />
                <div v-if="imageError" class="image-error">
                  <i class="fas fa-exclamation-triangle"></i>
                  Error al cargar la imagen
                </div>
              </div>
              
              <div v-else class="image-upload">
                <input 
                  type="file" 
                  ref="imageInput"
                  accept="image/*"
                  @change="onImageChange"
                  class="image-input"
                  id="comprobante-input"
                />
                <label for="comprobante-input" class="image-upload-label">
                  <i class="fas fa-cloud-upload-alt"></i>
                  <span>Seleccionar imagen del comprobante</span>
                </label>
                <div v-if="uploadingImage" class="upload-progress">
                  <i class="fas fa-spinner fa-spin"></i>
                  Subiendo imagen...
                </div>
              </div>
            </div>
          </div>

          <!-- Acciones -->
          <div class="form-actions">
            <button type="button" class="btn-secondary" @click="cerrarValidarModal">
              <i class="fas fa-times me-1"></i>
              Cancelar
            </button>
            <button type="submit" class="btn-success" :disabled="!canValidate">
              <i class="fas fa-check me-1"></i>
              Validar Pago
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import axios from 'axios'
import { notifySuccess, notifyError } from '@/services/notificationService'

export default {
  name: 'PagosList',
  setup() {
        // Modal para validación manual
        const showValidarModal = ref(false)
        const pagoAValidar = ref(null)
        const validationForm = ref({
          fecha_pago_real: '',
          monto_depositado: '',
          codigo_transaccion: '',
          metodo_pago: '',
          imagen_url: '',
          imagen_file: null
        })
        const uploadingImage = ref(false)
        const imageInput = ref(null)
        const imageError = ref(false)

        function abrirValidarPago(pago) {
          pagoAValidar.value = pago
          imageError.value = false
          validationForm.value = {
            fecha_pago_real: '',
            monto_depositado: pago.valor,
            codigo_transaccion: '',
            metodo_pago: pago.metodo_pago || '',
            imagen_url: pago.comprobante_url || '',
            imagen_file: null
          }
          showValidarModal.value = true
        }
        
        function cerrarValidarModal() {
          showValidarModal.value = false
          pagoAValidar.value = null
          validationForm.value = {
            fecha_pago_real: '',
            monto_depositado: '',
            codigo_transaccion: '',
            imagen_url: '',
            imagen_file: null
          }
          loadPagos(paginacion.value.pagina_actual)
          loadMetricas()
        }

        // Funciones para el modal de validación
        const canValidate = computed(() => {
          return validationForm.value.fecha_pago_real && 
                 validationForm.value.monto_depositado && 
                 validationForm.value.codigo_transaccion &&
                 (validationForm.value.imagen_url || validationForm.value.imagen_file)
        })

        const onImageChange = async (event) => {
          const file = event.target.files[0]
          if (file) {
            validationForm.value.imagen_file = file
            uploadingImage.value = true
            
            try {
              const formData = new FormData()
              formData.append('file', file)

              const response = await axios.post('/api/upload/comprobante', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
              })

              if (response.data.success) {
                validationForm.value.imagen_url = response.data.url
                notifySuccess('Imagen subida correctamente')
              } else {
                notifyError('Error al subir la imagen')
              }
            } catch (error) {
              console.error('Error uploading image:', error)
              notifyError('Error al subir la imagen')
            } finally {
              uploadingImage.value = false
            }
          }
        }

        const onImageError = () => {
          imageError.value = true
          console.error('Error al cargar la imagen:', validationForm.value.imagen_url)
        }

        const onImageLoad = () => {
          imageError.value = false
        }

        const validarPago = async () => {
          if (!canValidate.value) {
            notifyError('Complete todos los campos requeridos')
            return
          }

          try {
            const response = await axios.post(`/api/admin/pagos/${pagoAValidar.value.id}/validar`, {
              fecha_pago_real: validationForm.value.fecha_pago_real,
              monto_depositado: validationForm.value.monto_depositado,
              codigo_transaccion: validationForm.value.codigo_transaccion,
              metodo_pago: validationForm.value.metodo_pago,
              imagen_url: validationForm.value.imagen_url
            })

            if (response.data.success) {
              notifySuccess('Pago validado correctamente')
              cerrarValidarModal()
            } else {
              notifyError(response.data.message || 'Error al validar el pago')
            }
          } catch (error) {
            console.error('Error validating payment:', error)
            notifyError('Error al validar el pago')
          }
        }

    const pagos = ref([])
    const metricas = ref({
      completados_mes: 0,
      pendientes: 0,
      fallidos: 0,
      reembolsos: 0
    })
    const loadingMetricas = ref(false)
    
    const filtros = ref({
      estado: '',
      metodo: '',
      fecha_desde: '',
      fecha_hasta: '',
      empresa: ''
    })
    
    const paginacion = ref({
      pagina_actual: 1,
      total_paginas: 1,
      paginas_visibles: []
    })
    
    const showDetalleModal = ref(false)
    const pagoSeleccionado = ref(null)

    const pagosFiltrados = computed(() => {
      let filtered = pagos.value

      if (filtros.value.estado) {
        filtered = filtered.filter(p => p.estado === filtros.value.estado)
      }
      if (filtros.value.metodo) {
        filtered = filtered.filter(p => (p.suscripcion_plan || 'Mensual').toLowerCase() === filtros.value.metodo.toLowerCase())
      }
      if (filtros.value.empresa) {
        const busqueda = filtros.value.empresa.toLowerCase()
        filtered = filtered.filter(p => 
          p.empresa_nombre.toLowerCase().includes(busqueda)
        )
      }

      return filtered
    })

    const loadPagos = async (pagina = 1) => {
      try {
        const params = {
          pagina,
          ...filtros.value
        }
        const response = await axios.get('/api/admin/pagos', { params })
        pagos.value = response.data.pagos
        paginacion.value = response.data.paginacion
      } catch (error) {
        console.error('Error loading pagos:', error)
      }
    }

    const loadMetricas = async () => {
      loadingMetricas.value = true
      try {
        const response = await axios.get('/api/admin/pagos/metricas')
        if (response.data && response.data.success && response.data.data) {
          metricas.value = {
            completados_mes: response.data.data.completados_mes || 0,
            pendientes: response.data.data.pendientes || 0,
            fallidos: response.data.data.fallidos || 0,
            reembolsos: response.data.data.reembolsos || 0
          }
        } else {
          console.warn('Respuesta de métricas inválida:', response.data)
        }
      } catch (error) {
        console.error('Error loading métricas:', error)
        // Mantener valores por defecto en caso de error
      } finally {
        loadingMetricas.value = false
      }
    }

    const verDetalle = async (pago) => {
      try {
        const response = await axios.get(`/api/admin/pagos/${pago.id}/detalle`)
        pagoSeleccionado.value = response.data
        showDetalleModal.value = true
      } catch (error) {
        console.error('Error loading detalle pago:', error)
      }
    }

    const procesarPago = async (pagoId) => {
      const result = await window.Swal.fire({
        title: 'Procesar Pago',
        text: '¿Está seguro de procesar este pago pendiente?',
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#28a745',
        cancelButtonColor: '#6c757d',
        confirmButtonText: 'Sí, procesar',
        cancelButtonText: 'Cancelar'
      })

      if (!result.isConfirmed) return
      
      try {
        await axios.post(`/api/admin/pagos/${pagoId}/procesar`)
        notifySuccess('Pago procesado exitosamente')
        loadPagos(paginacion.value.pagina_actual)
        loadMetricas()
      } catch (error) {
        console.error('Error procesando pago:', error)
        notifyError('Error al procesar el pago')
      }
    }

    const reembolsarPago = async (pagoId) => {
      const result = await window.Swal.fire({
        title: 'Reembolsar Pago',
        text: '¿Está seguro de reembolsar este pago? Esta acción no se puede deshacer.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#dc3545',
        cancelButtonColor: '#6c757d',
        confirmButtonText: 'Sí, reembolsar',
        cancelButtonText: 'Cancelar'
      })

      if (!result.isConfirmed) return
      
      try {
        await axios.post(`/api/admin/pagos/${pagoId}/reembolsar`)
        notifySuccess('Pago reembolsado exitosamente')
        loadPagos(paginacion.value.pagina_actual)
        loadMetricas()
      } catch (error) {
        console.error('Error reembolsando pago:', error)
        notifyError('Error al reembolsar el pago')
      }
    }

    const procesarPagosPendientes = async () => {
      const result = await window.Swal.fire({
        title: 'Procesar Pagos Pendientes',
        text: '¿Está seguro de procesar todos los pagos pendientes?',
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#28a745',
        cancelButtonColor: '#6c757d',
        confirmButtonText: 'Sí, procesar todos',
        cancelButtonText: 'Cancelar'
      })

      if (!result.isConfirmed) return
      
      try {
        await axios.post('/api/admin/pagos/procesar-pendientes')
        notifySuccess('Pagos pendientes procesados exitosamente')
        loadPagos(paginacion.value.pagina_actual)
        loadMetricas()
      } catch (error) {
        console.error('Error procesando pagos pendientes:', error)
        notifyError('Error al procesar los pagos pendientes')
      }
    }

    const descargarRecibo = async (pagoId) => {
      try {
        const response = await axios.get(`/api/admin/pagos/${pagoId}/recibo`, {
          responseType: 'blob'
        })
        
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `recibo_${pagoId}.pdf`)
        document.body.appendChild(link)
        link.click()
        link.remove()
      } catch (error) {
        console.error('Error descargando recibo:', error)
      }
    }

    const exportarReporte = async () => {
      try {
        const response = await axios.get('/api/admin/pagos/exportar', {
          params: filtros.value,
          responseType: 'blob'
        })
        
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `pagos_${new Date().toISOString().split('T')[0]}.xlsx`)
        document.body.appendChild(link)
        link.click()
        link.remove()
      } catch (error) {
        console.error('Error exportando reporte:', error)
      }
    }

    const cambiarPagina = (pagina) => {
      if (pagina >= 1 && pagina <= paginacion.value.total_paginas) {
        loadPagos(pagina)
      }
    }

    const aplicarFiltros = () => {
      loadPagos(1)
    }

    const limpiarFiltros = () => {
      filtros.value = {
        estado: '',
        metodo: '',
        fecha_desde: '',
        fecha_hasta: '',
        empresa: ''
      }
      loadPagos(1)
    }

    const getEstadoBadgeClass = (estado) => {
      const classes = {
        completado: 'bg-success',
        pendiente: 'bg-warning',
        en_verificacion: 'bg-info',
        fallido: 'bg-danger',
        reembolsado: 'bg-info'
      }
      return classes[estado] || 'bg-secondary'
    }

    const formatMetodoPago = (metodo) => {
      const labels = {
        suscripcion: 'Suscripción',
        tarjeta: 'Tarjeta',
        transferencia: 'Transferencia',
        paypal: 'PayPal',
        stripe: 'Stripe'
      }
      return labels[metodo] || metodo.charAt(0).toUpperCase() + metodo.slice(1)
    }

    const getMetodoIcon = (metodo) => {
      const icons = {
        suscripcion: 'fas fa-calendar-alt',
        tarjeta: 'bi bi-credit-card',
        transferencia: 'bi bi-bank',
        paypal: 'bi bi-paypal',
        stripe: 'bi bi-stripe'
      }
      return icons[metodo] || 'bi bi-credit-card-2-front'
    }

    const formatDateTime = (date) => {
      return date ? new Date(date).toLocaleString('es-ES') : '-'
    }

    const formatDate = (date) => {
      return date ? new Date(date).toLocaleDateString('es-ES') : '-'
    }

    onMounted(() => {
      loadPagos()
      loadMetricas()
    })

    return {
      pagos,
      pagosFiltrados,
      metricas,
      loadingMetricas,
      filtros,
      paginacion,
      showDetalleModal,
      pagoSeleccionado,
      verDetalle,
      procesarPago,
      reembolsarPago,
      procesarPagosPendientes,
      descargarRecibo,
      exportarReporte,
      cambiarPagina,
      aplicarFiltros,
      limpiarFiltros,
      getEstadoBadgeClass,
      getMetodoIcon,
      formatMetodoPago,
      formatDateTime,
      // validación manual
      showValidarModal,
      pagoAValidar,
      abrirValidarPago,
      cerrarValidarModal,
      validationForm,
      uploadingImage,
      imageInput,
      imageError,
      canValidate,
      onImageChange,
      onImageError,
      onImageLoad,
      validarPago,
      formatDate,
    }
  }
}
</script>

<style>
/* Variables CSS para el tema */
:root {
  --primary-color: #3498db;
  --success-color: #27ae60;
  --warning-color: #f39c12;
  --danger-color: #e74c3c;
  --info-color: #17a2b8;
  --secondary-color: #6c757d;
}

/* Contenedor principal */
.pagos-container {
  background-color: var(--bg-main);
  color: var(--text-primary);
  min-height: 100vh;
  padding: 1.5rem;
}

/* Header de la página */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: var(--card-bg);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  box-shadow: 0 2px 4px var(--shadow-color);
}

.header-content {
  flex: 1;
}

.page-title {
  font-size: 1.75rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 0.5rem 0;
  display: flex;
  align-items: center;
}

.page-subtitle {
  font-size: 0.95rem;
  color: var(--text-secondary);
  margin: 0;
  display: flex;
  align-items: center;
}

.header-actions {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
}

/* Wrapper de contenido */
.content-wrapper {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

/* Grid de métricas */
.metrics-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
}

.metric-card {
  background: var(--card-bg);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  padding: 1.5rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px var(--shadow-color);
}

.metric-card:hover {
  box-shadow: 0 4px 12px var(--shadow-color);
  transform: translateY(-2px);
}

.metric-icon {
  width: 60px;
  height: 60px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.metric-icon i {
  font-size: 1.5rem;
  color: white;
}

.metric-completed .metric-icon {
  background: linear-gradient(135deg, var(--success-color), #2ecc71);
}

.metric-pending .metric-icon {
  background: linear-gradient(135deg, var(--warning-color), #e67e22);
}

.metric-failed .metric-icon {
  background: linear-gradient(135deg, var(--danger-color), #c0392b);
}

.metric-refund .metric-icon {
  background: linear-gradient(135deg, var(--info-color), #1f8dd6);
}

.metric-content {
  flex: 1;
}

.metric-number {
  font-size: 2rem;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 0.25rem;
}

.metric-label {
  font-size: 0.9rem;
  color: var(--text-secondary);
  font-weight: 500;
}

/* Sección de filtros */
.filters-section {
  background: var(--card-bg);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  padding: 1.5rem;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 1rem;
  box-shadow: 0 2px 4px var(--shadow-color);
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.filter-actions {
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  gap: 0.5rem;
}

.filter-label {
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--text-primary);
}

.filter-select,
.filter-input {
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  background: var(--input-bg);
  color: var(--text-primary);
  font-size: 0.9rem;
  transition: all 0.3s ease;
}

.filter-select:focus,
.filter-input:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 3px rgba(52, 152, 219, 0.1);
}

/* Botones personalizados */
.btn-primary-custom,
.btn-success-custom,
.btn-secondary-custom,
.btn-danger-outline {
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 500;
  font-size: 0.9rem;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  text-decoration: none;
  transition: all 0.3s ease;
  cursor: pointer;
  border: none;
}

.btn-primary-custom {
  background: linear-gradient(135deg, var(--primary-color), #2980b9);
  color: white;
}

.btn-primary-custom:hover {
  background: linear-gradient(135deg, #2980b9, var(--primary-color));
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(52, 152, 219, 0.3);
}

.btn-success-custom {
  background: linear-gradient(135deg, var(--success-color), #2ecc71);
  color: white;
}

.btn-success-custom:hover {
  background: linear-gradient(135deg, #2ecc71, var(--success-color));
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(39, 174, 96, 0.3);
}

.btn-secondary-custom {
  background: var(--secondary-color);
  color: white;
}

.btn-secondary-custom:hover {
  background: #5a6268;
  transform: translateY(-1px);
}

.btn-danger-outline {
  background: transparent;
  color: var(--danger-color);
  border: 1px solid var(--danger-color);
}

.btn-danger-outline:hover {
  background: var(--danger-color);
  color: white;
}

/* Sección de tabla */
.table-section {
  background: var(--card-bg);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  box-shadow: 0 2px 4px var(--shadow-color);
  overflow: hidden;
}

.table-container {
  overflow-x: auto;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
}

.data-table th {
  background: var(--secondary-bg-main);
  color: var(--text-primary);
  padding: 1rem;
  text-align: left;
  font-weight: 600;
  font-size: 0.9rem;
  border-bottom: 2px solid var(--border-color);
  white-space: nowrap;
}

.data-table td {
  padding: 1rem;
  border-bottom: 1px solid var(--border-color);
  color: var(--text-primary);
  vertical-align: middle;
}

.data-table tbody tr {
  transition: background-color 0.2s ease;
}

.data-table tbody tr:hover {
  background: var(--hover-bg);
}

/* Elementos específicos de la tabla */
.transaction-id {
  background: var(--secondary-bg-main);
  color: var(--text-primary);
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-family: 'Courier New', monospace;
  font-size: 0.85rem;
}

.company-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.company-name {
  font-weight: 600;
  color: var(--text-primary);
}

.company-plan {
  font-size: 0.85rem;
  color: var(--text-secondary);
}

.payment-date {
  font-size: 0.9rem;
  color: var(--text-primary);
}

.amount-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.amount-value {
  font-weight: 600;
  color: var(--text-primary);
}

.amount-currency {
  font-size: 0.85rem;
  color: var(--text-secondary);
}

.payment-method {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  color: var(--text-primary);
}

.error-message {
  font-size: 0.8rem;
  color: var(--danger-color);
  margin-top: 0.25rem;
}

.reference-info {
  font-size: 0.85rem;
  color: var(--text-secondary);
}

/* Badges de estado */
.status-badge {
  padding: 0.375rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.bg-success {
  background: rgba(39, 174, 96, 0.1);
  color: var(--success-color);
  border: 1px solid rgba(39, 174, 96, 0.2);
}

.bg-warning {
  background: rgba(243, 156, 18, 0.1);
  color: var(--warning-color);
  border: 1px solid rgba(243, 156, 18, 0.2);
}

.bg-danger {
  background: rgba(231, 76, 60, 0.1);
  color: var(--danger-color);
  border: 1px solid rgba(231, 76, 60, 0.2);
}

.bg-info {
  background: rgba(23, 162, 184, 0.1);
  color: var(--info-color);
  border: 1px solid rgba(23, 162, 184, 0.2);
}

.bg-secondary {
  background: rgba(108, 117, 125, 0.1);
  color: var(--secondary-color);
  border: 1px solid rgba(108, 117, 125, 0.2);
}

/* Botones de acción */
.action-buttons {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.action-btn {
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  cursor: pointer;
}

.action-view {
  background: rgba(52, 152, 219, 0.1);
  color: var(--primary-color);
}

.action-view:hover {
  background: var(--primary-color);
  color: white;
  transform: scale(1.1);
}

.action-process {
  background: rgba(39, 174, 96, 0.1);
  color: var(--success-color);
}

.action-process:hover {
  background: var(--success-color);
  color: white;
  transform: scale(1.1);
}

.action-refund {
  background: rgba(243, 156, 18, 0.1);
  color: var(--warning-color);
}

.action-refund:hover {
  background: var(--warning-color);
  color: white;
  transform: scale(1.1);
}

.action-download {
  background: rgba(23, 162, 184, 0.1);
  color: var(--info-color);
}

.action-download:hover {
  background: var(--info-color);
  color: white;
  transform: scale(1.1);
}

/* Paginación */
.pagination-section {
  background: var(--card-bg);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 4px var(--shadow-color);
}

.pagination-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
}

.pagination-btn {
  padding: 0.75rem 1rem;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  background: var(--card-bg);
  color: var(--text-primary);
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s ease;
  cursor: pointer;
}

.pagination-btn:hover:not(.disabled) {
  background: var(--primary-color);
  color: white;
  border-color: var(--primary-color);
}

.pagination-btn.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pagination-pages {
  display: flex;
  gap: 0.5rem;
}

.pagination-page {
  width: 40px;
  height: 40px;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  background: var(--card-bg);
  color: var(--text-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  cursor: pointer;
}

.pagination-page:hover {
  background: var(--primary-color);
  color: white;
  border-color: var(--primary-color);
}

.pagination-page.active {
  background: var(--primary-color);
  color: white;
  border-color: var(--primary-color);
}

/* Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
  padding: 2rem;
}

.modal-container {
  background: var(--card-bg);
  border-radius: 16px;
  max-width: 900px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 2rem 2rem 1rem 2rem;
  border-bottom: 1px solid var(--border-color);
}

.modal-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}

.modal-close {
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 8px;
  background: var(--danger-color);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
}

.modal-close:hover {
  background: #c0392b;
  transform: scale(1.1);
}

.modal-body {
  padding: 2rem;
}

.modal-info-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  margin-bottom: 2rem;
}

.info-section h4 {
  color: var(--text-primary);
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid var(--primary-color);
}

.info-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 0;
  border-bottom: 1px solid var(--border-color);
}

.info-item:last-child {
  border-bottom: none;
}

.info-label {
  font-weight: 600;
  color: var(--text-secondary);
  flex-shrink: 0;
  min-width: 120px;
}

.info-value {
  color: var(--text-primary);
  text-align: right;
}

.transaction-code {
  background: var(--secondary-bg-main);
  color: var(--text-primary);
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-family: 'Courier New', monospace;
  font-size: 0.85rem;
}

.modal-divider {
  height: 1px;
  background: var(--border-color);
  margin: 2rem 0;
}

.gateway-section h4 {
  color: var(--text-primary);
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 1rem;
}

.gateway-details {
  background: var(--secondary-bg-main);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  padding: 1rem;
}

.gateway-details pre {
  font-size: 0.8rem;
  max-height: 200px;
  overflow-y: auto;
  margin: 0;
  color: var(--text-primary);
  background: transparent;
}

.modal-footer {
  padding: 1rem 2rem 2rem 2rem;
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  border-top: 1px solid var(--border-color);
}

/* Responsive */
@media (max-width: 768px) {
  .pagos-container {
    padding: 1rem;
  }
  
  .page-header {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }
  
  .header-actions {
    justify-content: flex-start;
  }
  
  .metrics-grid {
    grid-template-columns: 1fr;
  }
  
  .filters-section {
    grid-template-columns: 1fr;
  }
  
  .filter-actions {
    flex-direction: column;
  }
  
  .action-buttons {
    justify-content: center;
  }
  
  .pagination-wrapper {
    flex-direction: column;
    gap: 1rem;
  }
  
  .modal-overlay {
    padding: 1rem;
  }
  
  .modal-info-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
  
  .info-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;
  }
  
  .info-value {
    text-align: left;
  }
  
  .modal-footer {
    flex-direction: column;
  }
}

/* Spinner de carga para métricas */
.loading-spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid var(--border-color);
  border-radius: 50%;
  border-top-color: var(--primary-color);
  animation: spin 1s ease-in-out infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Modal de Validación de Pago */
.validar-pago-modal {
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid var(--border-color);
  background: var(--card-bg);
}

.modal-title {
  margin: 0;
  color: var(--text-primary);
  font-size: 1.25rem;
  font-weight: 600;
}

.modal-close {
  background: none;
  border: none;
  font-size: 1.25rem;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 0.375rem;
  transition: all 0.2s;
}

.modal-close:hover {
  background: var(--bg-secondary);
  color: var(--text-primary);
}

.modal-body {
  padding: 1.5rem;
}

.transaction-info {
  margin-bottom: 2rem;
  padding: 1rem;
  background: var(--bg-secondary);
  border-radius: 0.5rem;
  border: 1px solid var(--border-color);
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.info-label {
  font-weight: 600;
  color: var(--text-secondary);
  font-size: 0.875rem;
}

.info-value {
  color: var(--text-primary);
  font-weight: 500;
}

.info-value.amount {
  color: var(--success-color);
  font-size: 1.1rem;
}

.validation-form {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.form-section {
  padding: 1rem;
  background: var(--card-bg);
  border: 1px solid var(--border-color);
  border-radius: 0.5rem;
}

.section-title {
  margin: 0 0 1rem 0;
  color: var(--text-primary);
  font-size: 1rem;
  font-weight: 600;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-label {
  font-weight: 500;
  color: var(--text-primary);
  font-size: 0.875rem;
}

.form-input {
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 0.375rem;
  background: var(--input-bg);
  color: var(--text-primary);
  font-size: 0.875rem;
}

.form-input:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 2px rgba(52, 152, 219, 0.2);
}

.image-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.image-preview {
  width: 100%;
  max-width: 450px;
}

.preview-image {
  width: 100%;
  height: auto;
  border-radius: 0.5rem;
  border: 1px solid var(--border-color);
}

.image-error {
  color: var(--danger-color);
  font-size: 0.875rem;
  text-align: center;
  margin-top: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.image-upload {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  width: 100%;
}

.image-input {
  display: none;
}

.image-upload-label {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 2rem;
  border: 2px dashed var(--border-color);
  border-radius: 0.5rem;
  background: var(--bg-secondary);
  cursor: pointer;
  transition: all 0.2s;
  width: 100%;
  max-width: 300px;
}

.image-upload-label:hover {
  border-color: var(--primary-color);
  background: rgba(52, 152, 219, 0.05);
}

.image-upload-label i {
  font-size: 2rem;
  color: var(--text-secondary);
}

.upload-progress {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--primary-color);
  font-weight: 500;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  padding-top: 1rem;
  border-top: 1px solid var(--border-color);
}

.btn-secondary, .btn-success {
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 0.375rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-secondary {
  background: var(--secondary-color);
  color: white;
}

.btn-secondary:hover {
  background: #5a6268;
}

.btn-success {
  background: var(--success-color);
  color: white;
}

.btn-success:hover:not(:disabled) {
  background: #218838;
}

.btn-success:disabled {
  background: #6c757d;
  cursor: not-allowed;
  opacity: 0.6;
}

@media (max-width: 480px) {
  .page-title {
    font-size: 1.5rem;
  }
  
  .metric-number {
    font-size: 1.5rem;
  }
  
  .header-actions {
    flex-direction: column;
  }
  
  .pagination-pages {
    flex-wrap: wrap;
    justify-content: center;
  }
}
</style>
